import React, { useEffect, useRef, useState } from 'react';
import LogoutIcon from '@mui/icons-material/Logout';
import { Tooltip } from '@mui/material';
import { useLeaveTable } from 'hooks/useLeaveTable';
import { useOrientation } from 'hooks/useOrientation';
import { useSupportsHover } from 'hooks/useSupportsHover';

import LeaveConfirmPopover from './LeaveConfirmPopover';
import { tableControlButtonStyle, tableControlIconStyle } from './tableControlStyles';

// Re-exported from the shared hook so existing imports (and its unit test) keep
// working while the leave logic lives in one place.
export { shouldHideExitTableButton } from 'hooks/useLeaveTable';

const ExitTableButton = () => {
  // All the leave/gating logic is shared with the table menu panel.
  const { leave, isHidden, isWaitlistOnly, exitLabel } = useLeaveTable();
  const orientation = useOrientation();
  const isPortrait = orientation === 'portrait';
  const supportsHover = useSupportsHover();
  const [showConfirm, setShowConfirm] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showConfirm) return;
    const handleClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setShowConfirm(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('touchstart', handleClick as any);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('touchstart', handleClick as any);
    };
  }, [showConfirm]);

  const handleLeave = () => {
    setShowConfirm(false);
    leave();
  };

  if (isHidden) {
    return null;
  }

  const hoverHandlers = supportsHover
    ? {
        onMouseEnter: (e: React.MouseEvent) => {
          (e.currentTarget as HTMLElement).style.transform = 'scale(1.04)';
        },
        onMouseLeave: (e: React.MouseEvent) => {
          (e.currentTarget as HTMLElement).style.transform = 'scale(1)';
        }
      }
    : {};

  const iconSize = tableControlIconStyle(isPortrait);

  // The button is a native <button> — assistive tech reads its text or the
  // icon's implicit "Exit Table" label from aria-label when we render an
  // icon-only variant (landscape). The MUI Tooltip was doing two things:
  //   1. Providing a hover label on desktop landscape (icon-only mode).
  //   2. Duplicating the visible label on portrait — where the button
  //      already renders `<span>{exitLabel}</span>` as its child.
  //
  // Case (2) is a ghost-label bug on touch devices: MUI's touch listener
  // triggers the tooltip on tap / long-press, and some mobile browsers
  // don't reliably fire the dismiss trigger (blur / touchend) when a
  // sibling popover — LeaveConfirmPopover — captures focus, leaving the
  // "Exit Table" label hanging next to the button. Reported in the wild
  // via a screenshot showing the duplicate.
  //
  // Fix: only wrap in Tooltip when the button is icon-only (landscape),
  // and disable the touch + focus listeners so a long-press or programmatic
  // focus can't invoke it. Hover on a real pointer stays intact.
  const button = (
    <button
      onClick={() => setShowConfirm(true)}
      aria-label={exitLabel}
      style={tableControlButtonStyle(isPortrait, 'exit')}
      {...hoverHandlers}>
      {!isPortrait && <LogoutIcon style={iconSize} />}
      {isPortrait && <span>{exitLabel}</span>}
    </button>
  );

  return (
    <div ref={wrapperRef} style={{ position: 'relative' }}>
      {isPortrait ? (
        button
      ) : (
        <Tooltip title={exitLabel} disableTouchListener disableFocusListener>
          {button}
        </Tooltip>
      )}

      {showConfirm && (
        <LeaveConfirmPopover
          isWaitlistOnly={isWaitlistOnly}
          onConfirm={handleLeave}
          onCancel={() => setShowConfirm(false)}
        />
      )}
    </div>
  );
};

export default ExitTableButton;
